import { PathStyleExtension } from "@deck.gl/extensions";
import {
  IconLayer,
  IconLayerProps,
  PathLayer,
  PathLayerProps,
  PolygonLayer,
  PolygonLayerProps,
} from "@deck.gl/layers";
import { FeatureGroupProps } from "react-leaflet";
import { useDispatch, useSelector } from "react-redux";

import { XYLocation } from "@nens/deck-gl";
import { doesNewPointCrossExistingLines } from "@nens/shared-utils";

import { AppDispatch } from "../../../App";
import { getBlockClick, getMouseLocation, setBlockClick } from "../../../state/map";
import { ICON_MAPPING, STATIC_COLORS } from "../DeckGlConsts";

export interface DrawPolygonProps extends FeatureGroupProps {
  setCallback: (points: XYLocation[]) => void;
  confirmDrawingCallback: () => void;
  removeLastPointCallback: () => void;
  points?: XYLocation[];
  existingRasterEdits: XYLocation[][];
  defaultPolygonLayerProps: PolygonLayerProps;
  defaultPathLayerProps: PathLayerProps;
  finished?: boolean;
}
export const DrawPolygon = ({
  setCallback,
  confirmDrawingCallback,
  removeLastPointCallback,
  points = [],
  existingRasterEdits,
  defaultPolygonLayerProps,
  defaultPathLayerProps,
  finished,
}: DrawPolygonProps) => {
  const dispatch = useDispatch() as AppDispatch;
  const mouseLocation = useSelector(getMouseLocation);

  const dashLine =
    mouseLocation && points.length > 0 ? [points[points.length - 1], mouseLocation] : null;
  const newLineIntersects = mouseLocation
    ? doesNewPointCrossExistingLines(mouseLocation, points)
    : false;

  const blockClick = useSelector(getBlockClick);
  !blockClick && newLineIntersects && dispatch(setBlockClick("POLYGON"));
  blockClick === "POLYGON" && !newLineIntersects && dispatch(setBlockClick(null));

  const defaultIconLayerProps = {
    iconAtlas: "./assets/images/iconAtlas.png",
    iconMapping: ICON_MAPPING,
    sizeUnits: "pixels",
    getSize: 25,
    getPosition: (d: any) => {
      return d.coordinates;
    },
    pickable: true,
    billboard: false,
    visible: !finished,
  } as unknown as IconLayerProps;

  const createIconData = (title: string, beginning?: boolean) =>
    points && points.length
      ? [
          {
            coordinates: beginning ? points[0] : points[points.length - 1],
            title,
          },
        ]
      : [];

  const confirmData = createIconData("Confirm drawing", true);
  const cancelData = createIconData("Cancel drawing");

  return [
    new PolygonLayer({
      ...defaultPolygonLayerProps,
      id: "POLYGON-existing-polygon-layer",
      data: existingRasterEdits,
    }),
    new PolygonLayer({
      ...defaultPolygonLayerProps,
      id: "POLYGON-new-polygon-layer",
      data: [points],
    } as PathLayerProps),
    new PathLayer({
      ...defaultPathLayerProps,
      id: "POLYGON-dash-polygon-layer",
      data: dashLine && !finished ? [dashLine] : [],
      getColor: () =>
        !newLineIntersects ? STATIC_COLORS.LEVEE_EDIT : STATIC_COLORS.LEVEE_EDIT_NOT_ALLOWED,
      getDashArray: [10, 10],
      dashJustified: true,
      extensions: [new PathStyleExtension({ dash: true })],
      visible: !finished,
    } as PathLayerProps),
    new IconLayer({
      ...defaultIconLayerProps,
      id: "POLYGON-confirm-polygon-layer",
      data: confirmData,
      getIcon: () => "confirmNoAnchor",
      onClick: () => {
        confirmDrawingCallback();
        return true;
      },
      visible: defaultIconLayerProps.visible && points.length > 2,
    }),
    new IconLayer({
      ...defaultIconLayerProps,
      id: "POLYGON-cancel-polygon-layer",
      data: cancelData,
      getIcon: () => "cancelNoAnchor",
      onClick: () => {
        removeLastPointCallback();
        return true;
      },
    }),
  ];
};
